import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconFilter } from "../../../../common/icons/IconFilter/IconFilter";
import {
  LosFilterModal,
  LosFilterState,
  countActiveFilters,
  DEFAULT_FILTER_STATE,
} from "../LosFilterModal/LosFilterModal";
import type { LosUiHeaderFilterFlags } from "../LosFilterModal/LosFilterModal";
import defaultContent from "../../../../content/los";
import "./LosFilterButton.scss";

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LosFilterButtonProps {
  /** Feature flags controlling which filter rows are shown in the modal */
  uiHeader: LosUiHeaderFilterFlags;
  /** The current committed filter state */
  filterState: LosFilterState;
  /** When true, Volume and ZeroVolume filter rows are hidden in the modal */
  inTreeView: boolean;
  /** When true, the button is disabled and cannot be clicked */
  disabled: boolean;
  /** Called with the new filter state when the user clicks Apply in the modal */
  onApply: (next: LosFilterState) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const LosFilterButton = ({
  uiHeader,
  filterState = DEFAULT_FILTER_STATE,
  inTreeView,
  disabled,
  onApply,
}: LosFilterButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation(["los"], { useSuspense: false });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const activeCount = countActiveFilters(filterState);
  const badgeLabel = activeCount > 99 ? "99+" : String(activeCount);
  const ariaLabel =
    activeCount > 0
      ? t("toolbar.filtersActiveAriaLabel", defaultContent.toolbar.filtersActiveAriaLabel).replace(
          "{count}",
          String(activeCount),
        )
      : t("toolbar.filtersAriaLabel", defaultContent.toolbar.filtersAriaLabel);

  const handleButtonClick = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleApply = (next: LosFilterState) => {
    onApply(next);
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <div className="LosFilterButton">
      <button
        ref={buttonRef}
        type="button"
        className={`LosToolbar__iconButton${isOpen ? " LosToolbar__iconButton--active" : ""}`}
        onClick={handleButtonClick}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        data-testid="los-filter-button"
      >
        <IconFilter />
        {activeCount > 0 && (
          <span className="LosToolbar__filterBadge" aria-hidden="true" data-testid="los-filter-badge">
            {badgeLabel}
          </span>
        )}
      </button>

      <LosFilterModal
        show={isOpen}
        uiHeader={uiHeader}
        filterState={filterState}
        inTreeView={inTreeView}
        onApply={handleApply}
        onClose={handleClose}
        triggerRef={buttonRef}
      />
    </div>
  );
};
